// eslint-disable-next-line
import { injectable } from "inversify";
import {
    AccountLinkResponseDto,
    IActionStoreValue,
    IIntegration,
    IIntegrationCategoryPreset,
    INTEGRATION_CODE,
} from "../../models/IntegrationsTypes";
import { TYPE, rootContainer } from '../container/root';
import { IApiResponse, IHttpService } from "../http-service";
import { ICanadaPostMerchantCredentials, ICanadaPostOAuthCredentials } from "../../pages/integrations/products-services/stores/credentials/shipping/canada-post/canada-post-oauth-credentials-store";
import { WithOld } from "../../stores/types";

export interface IIntegrationsService {}

interface IOauth1Token {
    oauth_token: string;
    oauth_token_secret: string;
}

interface IRequestOauth1Body {
    serviceCode: INTEGRATION_CODE;
    redirectUri?: string;
    baseUri?: string;
    accountId?: string;
    state?: string;
    nonce?: string;
    clientId?: string;
    clientSecret?: string;
    tokenSecret?: string;
}

@injectable()
class IntegrationsService implements IIntegrationsService {
    private readonly httpService: IHttpService = rootContainer.get<IHttpService>(TYPE.ApiHttp);

    async getIntegrationsPresets(integration_code?: string): Promise<IIntegrationCategoryPreset[]> {
        const searchParams = new URLSearchParams();
        if (integration_code) {
            searchParams.append('integration_code', integration_code);
        }

        const res = await this.httpService.get<IApiResponse<IIntegrationCategoryPreset[]>>(`/rms-api/integrations/presets?${searchParams.toString()}`);
        return res.payload;
    }

    async getIntegrations(): Promise<IIntegration[]> {
        const res = await this.httpService.get<IApiResponse<IIntegration[]>>('/rms-api/integrations');
        return res.payload;
    }

    async registerIntegration<CredentialsType>(credentials: CredentialsType, service_code: string, service_type: string, actions: IActionStoreValue, is_sandbox: boolean = false): Promise<IIntegration> {
        const res = await this.httpService.put<IApiResponse<IIntegration>>('/rms-api/integrations/register', {
            credentials,
            service_code,
            service_type,
            actions,
            is_sandbox,
        });
        return res.payload;
    }

    async removeIntegration(data: WithOld<IIntegration>): Promise<IIntegration> {
        const res = await this.httpService.remove<IApiResponse<IIntegration>>('/rms-api/integrations', data);
        return res.payload;
    }

    async updateIntegration(integration: WithOld<IIntegration>): Promise<IIntegration> {
        const res = await this.httpService.put<IApiResponse<IIntegration>>('/rms-api/integrations', {
            ...integration,
        });
        return res.payload;
    }

    async requestOauth1Token(body: IRequestOauth1Body): Promise<IOauth1Token> {
        const res = await this.httpService.post<IOauth1Token>('/rms-api/integrations/oauth1-token', body);
        return res;
    }

    async getCanadaPostOauthCredentials(): Promise<ICanadaPostOAuthCredentials> {
        const res = await this.httpService.get<IApiResponse<ICanadaPostOAuthCredentials>>('/rms-api/integrations/canada-post-oauth-credentials');
        return res.payload;
    }

    async getCanadaPostMerchantCredentials(token: string): Promise<ICanadaPostMerchantCredentials> {
        const res = await this.httpService.get<IApiResponse<ICanadaPostMerchantCredentials>>(`/rms-api/integrations/canada-post-merchant-credentials/${token}`);
        return res.payload;
    }

    async createAccountLink(serviceCode: string, credentials: Record<string, any> | null = null, is_sandbox = false): Promise<AccountLinkResponseDto> {
        const body = {
            is_sandbox,
            credentials,
        };
        const res = await this.httpService.post<IApiResponse<AccountLinkResponseDto>>(`/rms-api/integrations/create-account-link/${serviceCode}`, body);
        return res.payload;
    }
}

export default IntegrationsService;
